/**
 * ═══════════════════════════════════════════════════════════════════════════
 * RESTAURANT DETAIL PAGE - MOCKUP V3 REBUILD
 * ═══════════════════════════════════════════════════════════════════════════
 * 
 * Complete structural rebuild:
 * - HeroCover: Full image + gradient + meta IN overlay
 * - FloatingActionBar: Glass morphism, overlaps hero
 * - SegmentedTabs: iOS underline style
 * - MenuPreviewCTA: SINGLE menu preview (no duplicates)
 * - Desktop: Two-column layout with sidebar
 * 
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { Restaurant } from '@/lib/types';
import { Utensils } from 'lucide-react';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { useRestaurantFavorite } from '@/hooks/use-favorites';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { getOpenStatus } from '@/utils/restaurantStatus';
import { useTimeTick } from '@/hooks/useTimeTick';
import { safeParseResponse } from '@/lib/queryClient';
import SEO from '@/components/SEO';
import { useAnalytics } from '@/hooks/useAnalytics';

// NEW Component Structure - Mockup V3
import { 
  HeroCover, 
  FloatingActionBar, 
  SegmentedTabs,
  HighlightsPills,
  MenuPreviewCTA,
  MenuGalleryWrapper,
  StickyBottomCTA,
  InfoContent,
  ReviewsContent,
  DesktopSidebar,
  type TabId
} from '@/components/restaurant-detail';

// ═══════════════════════════════════════════════════════════════════════════
// LOADING SKELETON
// ═══════════════════════════════════════════════════════════════════════════
const RestaurantSkeleton = () => (
  <main className="min-h-screen" style={{ background: '#F8FAF9' }}>
    {/* Hero Skeleton */}
    <div className="relative w-full h-[340px] bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-pulse" />
    
    {/* Floating Bar Skeleton */}
    <div className="mx-4 -mt-8 relative z-10">
      <div 
        className="rounded-3xl p-5"
        style={{ background: 'rgba(255,255,255,0.8)' }}
      >
        <div className="flex justify-around">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex flex-col items-center gap-2">
              <Skeleton className="h-12 w-12 rounded-2xl" />
              <Skeleton className="h-3 w-14" />
            </div>
          ))}
        </div>
      </div>
    </div>
    
    {/* Tabs Skeleton */}
    <div className="mt-5 border-b border-gray-100">
      <div className="flex">
        {[1, 2, 3].map(i => (
          <div key={i} className="flex-1 py-4 flex justify-center">
            <Skeleton className="h-4 w-20" />
          </div>
        ))}
      </div>
    </div>
    
    {/* Content Skeleton */}
    <div className="px-4 py-5 space-y-4">
      <div className="flex gap-2">
        <Skeleton className="h-9 w-40 rounded-full" />
        <Skeleton className="h-9 w-36 rounded-full" />
      </div>
      <Skeleton className="h-[280px] w-full rounded-3xl" />
      <Skeleton className="h-14 w-full rounded-2xl" />
    </div>
  </main>
);

// ═══════════════════════════════════════════════════════════════════════════
// ERROR STATE
// ═══════════════════════════════════════════════════════════════════════════
interface ErrorStateProps {
  navigate: (path: string) => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ navigate }) => (
  <>
    <SEO 
      title="Restaurant nicht gefunden" 
      description="Das angeforderte Restaurant konnte nicht gefunden werden." 
    />
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: '#F8FAF9' }}>
      <div className="text-center max-w-sm">
        <div 
          className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6"
          style={{ background: 'rgba(14,127,79,0.08)' }}
        >
          <Utensils className="h-9 w-9 text-emerald-700" strokeWidth={1.5} />
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-3">Restaurant nicht gefunden</h1>
        <p className="text-gray-500 mb-8">Das Restaurant existiert nicht mehr oder wurde entfernt.</p>
        <Button 
          onClick={() => navigate('/')} 
          className="px-8 py-3 rounded-xl font-semibold"
          style={{
            background: 'linear-gradient(135deg, #0E7F4F 0%, #18B66A 100%)',
          }}
        >
          Zur Startseite
        </Button>
      </div>
    </div>
  </>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
const RestaurantDetailPage = () => {
  const { identifier } = useParams<{ identifier: string }>();
  const [, navigate] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const { track, trackAndFlush } = useAnalytics();
  
  // State
  const [activeTab, setActiveTab] = useState<TabId>('menu');
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);
  
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(identifier);

  // Data Fetching
  const { isLoading, error, data: restaurant } = useQuery<Restaurant | undefined>({
    queryKey: [isUuid ? `/api/restaurants/${identifier}` : `/api/restaurants/by-slug/${identifier}`, identifier],
    queryFn: async () => {
      let endpoint = isUuid
        ? `/api/restaurants/${identifier}?full=true`
        : `/api/restaurants/by-slug/${identifier}?full=true`;

      let response = await fetch(endpoint);
      
      if (!response.ok && !isUuid) {
        endpoint = `/api/restaurants/${identifier}?full=true`;
        response = await fetch(endpoint);
      }
      
      if (!response.ok) throw new Error('Restaurant nicht gefunden');
      
      const data = await safeParseResponse(response);
      if (!data) throw new Error("Leere Antwort vom Server");
      return data;
    },
    staleTime: 5 * 60 * 1000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  // Menu images
  const { data: menuImages = [] } = useQuery({
    queryKey: [`/api/restaurants/${restaurant?.id}/menus`],
    queryFn: async () => {
      if (!restaurant?.id) return [];
      const response = await fetch(`/api/restaurants/${restaurant.id}/menus`);
      if (!response.ok) return [];
      return await safeParseResponse(response) || [];
    },
    enabled: !!restaurant?.id,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Effects
  useEffect(() => {
    if (restaurant?.id) {
      track({ event_name: 'restaurant_profile_view', restaurant_id: restaurant.id });
    }
  }, [restaurant?.id, track]);

  useEffect(() => {
    if (isUuid && restaurant?.slug) {
      const newPath = `/restaurants/${restaurant.slug}`;
      if (window.location.pathname !== newPath) {
        window.history.replaceState({}, document.title, newPath);
      }
    }
  }, [isUuid, restaurant?.slug]);

  const tick = useTimeTick(45000);

  // Computed Values
  const openStatus = useMemo(() => {
    if (!restaurant) return { isOpen: false, nextChangeLabel: 'Öffnungszeiten nicht verfügbar' };
    return getOpenStatus(restaurant.openingHours || []);
  }, [restaurant?.openingHours, tick]);
  
  const hasDelivery = useMemo(() => {
    if (!restaurant) return false;
    return restaurant.delivery === true || 
           (restaurant as any).deliveryEnabled === true ||
           (restaurant as any).has_delivery === true;
  }, [restaurant]);

  const regularImages = useMemo(() => {
    return restaurant?.images?.filter(img => img.isMenu !== true) || [];
  }, [restaurant?.images]);
  
  // Profile image = sortOrder 1, hero/cover = first available image
  const profileImage = useMemo(() => {
    const profile = regularImages.find(img => img.sortOrder === 1);
    return profile?.path || null;
  }, [regularImages]);
  
  const heroImage = regularImages.length > 0 ? regularImages[0].path : null;
  
  const categoryNames = useMemo(() => {
    return restaurant?.categories?.slice(0, 2).map(c => c.name) || [];
  }, [restaurant?.categories]);

  const hasPhone = !!(restaurant?.phoneNumbers && restaurant.phoneNumbers.length > 0);
  const hasMenu = menuImages.length > 0;
  
  const address = restaurant 
    ? `${restaurant.street} ${restaurant.houseNumber}, ${restaurant.postalCode} ${restaurant.city}`
    : '';

  const openStatusText = openStatus.isOpen 
    ? 'Jetzt geöffnet'
    : openStatus.opensAt 
      ? `Öffnet um ${openStatus.opensAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr`
      : openStatus.nextChangeLabel;

  const closingTime = openStatus.isOpen && openStatus.closesAt
    ? openStatus.closesAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
    : undefined;

  // Handlers
  const handleBack = useCallback(() => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      navigate('/restaurants');
    }
  }, [navigate]);

  // Favorites hook with API persistence (replaces local useState)
  const restaurantId = restaurant?.id || '';
  const { isFavorite, toggle: toggleFavoriteHook } = useRestaurantFavorite(restaurantId);

  const handleToggleFavorite = useCallback(() => {
    if (!user) {
      toast({ title: "Anmeldung erforderlich", description: "Bitte melden Sie sich an." });
      navigate('/auth');
      return;
    }
    toggleFavoriteHook();
    toast({ title: !isFavorite ? "Als Favorit gespeichert!" : "Aus Favoriten entfernt" });
  }, [user, toast, navigate, toggleFavoriteHook, isFavorite]);

  const handleCall = useCallback(async () => {
    if (!restaurant) return;
    
    if (restaurant.phoneNumbers && restaurant.phoneNumbers.length > 0) {
      await trackAndFlush({ event_name: 'click_call', restaurant_id: restaurant.id });
      setTimeout(() => window.location.href = `tel:${restaurant.phoneNumbers![0].number}`, 100);
    } else {
      toast({ title: "Keine Telefonnummer", description: "Keine Nummer hinterlegt." });
    }
  }, [restaurant, trackAndFlush, toast]);

  const handleRoute = useCallback(async () => {
    if (!restaurant) return;
    
    await trackAndFlush({ event_name: 'click_route', restaurant_id: restaurant.id });
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      `${restaurant.name}, ${restaurant.street} ${restaurant.houseNumber}, ${restaurant.postalCode} ${restaurant.city}`
    )}`;
    setTimeout(() => window.open(mapsUrl, '_blank'), 100);
  }, [restaurant, trackAndFlush]);

  const handleReview = useCallback(() => {
    toast({ title: "Bewertung", description: "Bewertungsfunktion kommt bald!" });
  }, [toast]);

  const handleWebsite = useCallback(async () => {
    if (!restaurant?.website) return;
    
    await trackAndFlush({ event_name: 'click_website', restaurant_id: restaurant.id });
    const url = restaurant.website.startsWith('http') 
      ? restaurant.website 
      : `https://${restaurant.website}`;
    setTimeout(() => window.open(url, '_blank'), 100);
  }, [restaurant, trackAndFlush]);

  const handleOpenMenuGallery = useCallback(() => {
    if (!restaurant) return;
    track({ event_name: 'click_menu', restaurant_id: restaurant.id });
    setIsGalleryOpen(true);
  }, [restaurant, track]);

  const handleCloseMenuGallery = useCallback(() => {
    setIsGalleryOpen(false);
  }, []);

  const handleWriteReview = useCallback(() => {
    toast({ title: "Bewertung", description: "Funktion kommt bald!" });
  }, [toast]);

  // Loading
  if (isLoading) {
    return (
      <>
        <SEO title="Restaurant" description="Restaurant wird geladen..." />
        <RestaurantSkeleton />
      </>
    );
  }

  // Error
  if (error || !restaurant) {
    return <ErrorState navigate={navigate} />;
  }

  // SEO
  const restaurantCity = restaurant.city || '';
  const seoTitle = restaurantCity 
    ? `${restaurant.name} in ${restaurantCity}: Speisekarte`
    : `${restaurant.name}: Speisekarte & Öffnungszeiten`;
  const seoDescription = restaurantCity
    ? `Speisekarte, Öffnungszeiten, Adresse und Kontakt für ${restaurant.name} in ${restaurantCity}.`
    : `Speisekarte, Öffnungszeiten, Adresse und Kontakt für ${restaurant.name}.`;

  // Desktop Sidebar
  const desktopSidebar = (
    <DesktopSidebar
      hasPhone={hasPhone}
      hasMenu={hasMenu}
      isOpen={openStatus.isOpen}
      openStatusText={openStatusText}
      address={address}
      onCall={handleCall}
      onRoute={handleRoute}
      onReview={handleReview}
      onViewMenu={handleOpenMenuGallery}
    />
  );

  // ═══════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <>
      <SEO 
        title={seoTitle}
        description={seoDescription}
        canonical={`${typeof window !== 'undefined' ? window.location.origin : ''}/restaurants/${restaurant.slug || identifier}`}
      />
      
      <main className="min-h-screen" style={{ background: '#F5F6F5' }}>
        {/* Fluid container */}
        <div 
          className="w-full mx-auto"
          style={{ 
            maxWidth: '1440px',
          }}
        >
          {/* Hero - Full width */}
          <HeroCover
            restaurantName={restaurant.name}
            heroImage={heroImage}
            profileImage={profileImage}
            rating={restaurant.rating}
            ratingCount={restaurant.ratingCount}
            isOpen={openStatus.isOpen}
            openStatusText={openStatusText}
            isFavorite={isFavorite}
            onBack={handleBack}
            onToggleFavorite={handleToggleFavorite}
          />
          
          {/* Content Grid — pulled up slightly to overlap banner shadow */}
          <div 
            className="relative z-10 lg:grid lg:grid-cols-12 lg:gap-8"
            style={{ 
              padding: 'clamp(0px, 3vw, 40px)',
              marginTop: '-12px',
            }}
          >
            {/* Main Content - 8 cols on desktop */}
            <div className="lg:col-span-8">
              {/* Floating Action Bar - Mobile only (overlaps hero) */}
              <div className="lg:hidden">
                <FloatingActionBar
                  hasPhone={hasPhone}
                  onCall={handleCall}
                  onRoute={handleRoute}
                  onReview={handleReview}
                />
              </div>
              
              {/* Tabs - margin-top accounts for FAB overlap on mobile */}
              <div className="mt-5 lg:mt-0">
                <SegmentedTabs
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                />
              </div>
              
              {/* Tab Content */}
              <div style={{ paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))' }}>
                
                {/* SPEISEKARTE TAB */}
                {activeTab === 'menu' && (
                  <>
                    <HighlightsPills
                      categories={categoryNames}
                      isOpen={openStatus.isOpen}
                      closingTime={closingTime}
                      hasDelivery={hasDelivery}
                    />
                    
                    {/* SINGLE Menu Preview - No duplicates */}
                    <MenuPreviewCTA
                      menuImages={menuImages}
                      restaurantName={restaurant.name}
                      onOpenGallery={handleOpenMenuGallery}
                    />
                  </>
                )}
                
                {/* INFO TAB */}
                {activeTab === 'info' && (
                  <InfoContent
                    phoneNumbers={restaurant.phoneNumbers || []}
                    street={restaurant.street || ''}
                    houseNumber={restaurant.houseNumber || ''}
                    postalCode={restaurant.postalCode || ''}
                    city={restaurant.city || ''}
                    restaurantName={restaurant.name}
                    website={restaurant.website}
                    openingHours={restaurant.openingHours || []}
                    deliveryHours={restaurant.deliveryHours}
                    hasDelivery={hasDelivery}
                    skills={restaurant.skills || []}
                    description={restaurant.description}
                    categories={restaurant.categories || []}
                    onCall={handleCall}
                    onRoute={handleRoute}
                    onWebsite={handleWebsite}
                  />
                )}
                
                {/* REVIEWS TAB */}
                {activeTab === 'reviews' && (
                  <ReviewsContent onWriteReview={handleWriteReview} />
                )}
              </div>
            </div>
            
            {/* Desktop Sidebar - 4 cols */}
            <aside className="hidden lg:block lg:col-span-4">
              <div className="sticky" style={{ top: '24px' }}>
                {desktopSidebar}
              </div>
            </aside>
          </div>
        </div>
        
        {/* Sticky Bottom CTA - Mobile only */}
        <StickyBottomCTA
          hasPhone={hasPhone}
          hasMenu={hasMenu}
          onCall={handleCall}
          onViewMenu={handleOpenMenuGallery}
        />
        
        {/* Menu Gallery Modal */}
        <MenuGalleryWrapper
          menuImages={menuImages}
          restaurantName={restaurant.name}
          isOpen={isGalleryOpen}
          onClose={handleCloseMenuGallery}
        />
      </main>
    </>
  );
};

export default RestaurantDetailPage;
